import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import { collection, query, where, orderBy, limit, startAfter, getDocs, getFirestore, getCountFromServer } from 'firebase/firestore'
import ProductCard from '../components/ivystore/product/ProductCard'
import Button from 'react-bootstrap/Button'
import baseUrl from '../lib/base-url'
import { decorStyles } from '../lib/listing-manager/decor-styles'
import { colors } from '../lib/listing-manager/colors'
import { sofaTypes } from '../lib/listing-manager/sofa-types'
// import { brands } from '../lib/listing-manager/brands'
// import BreadCrumbs from '../components/ivystore/BreadCrumbs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { fetchSEO } from '../lib/blog/api'
import Seo from '../components/blog/seo'

const ITEMS_PER_PAGE = 12

const initialFilterValues = {
  minPrice: '',
  maxPrice: '',
  sofaType: '',
  color: '',
  decorStyle: '',
  // brand: '',
  state: '',
  city: '',
}

export const getServerSideProps = async () => {
  let stateOptions = []

  try {
    const geographyResponse = await fetch(`${baseUrl}/api/ivy-store/geography`)
    const { data: statesData } = await geographyResponse.json()

    stateOptions = statesData.map(state => ({
      slug: state.slug,
      name: state.name,
      cities: state.cities.filter(city => city.enabled).map(city => ({
        slug: city.slug,
        name: city.name,
      }))
    }))
  } catch (error) {
    console.log('Error fetching geography:', error.message)
  }

  const seo = await fetchSEO('ivystore-products')

  return {
    props: {
      seo,
      stateOptions
    }
  }
}

export default function IvyStoreProductsPage ({ seo, stateOptions }) {
  const router = useRouter()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState()
  const [totalPages, setTotalPages] = useState(1)
  const [pageRefs, setPageRefs] = useState({})

  // Filter states
  const [filters, setFilters] = useState(initialFilterValues)

  // Apply filters from URL parameters
  const initialRef = useRef(true)
  useEffect(() => {
    if (!initialRef.current) {
      return
    }

    // Initialize filters from URL parameters
    const {
      minPrice,
      maxPrice,
      sofaType,
      color,
      decorStyle,
      // brand,
      state,
      city,
    } = router.query
  
    setFilters({
      minPrice: minPrice || '',
      maxPrice: maxPrice || '',
      sofaType: sofaType || '',
      color: color || '',
      decorStyle: decorStyle || '',
      // brand: brand || '',
      state: state || '',
      city: city || '',
    })

    initialRef.current = false
  }, [router.query])
  
  // load on a page change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItems()
      updateUrlWithFilter(filters)
    }, 500)

    return () => clearTimeout(timer)
  }, [filters, currentPage])

  async function fetchItems () {
    if (currentPage === 1) {
      setPageRefs({})
    }

    try {
      setLoading(true)
      let q = query(
        collection(getFirestore(), 'ivystore_items'),
        where('hidden', '==', false)
      )

      // Apply filters
      if (filters.minPrice) q = query(q, where('data.price', '>=', parseFloat(filters.minPrice)))
      if (filters.maxPrice) q = query(q, where('data.price', '<=', parseFloat(filters.maxPrice)))
      if (filters.sofaType) q = query(q, where('data.sofaType', '==', filters.sofaType))
      if (filters.color) q = query(q, where('data.color', '==', filters.color))
      if (filters.decorStyle) q = query(q, where('data.decorStyle', '==', filters.decorStyle))
      // if (filters.brand) q = query(q, where('data.brand', '==', filters.brand))
      if (filters.state) q = query(q, where('store_data.location.state', '==', filters.state))
      if (filters.city) q = query(q, where('store_data.location.city', '==', filters.city))

      // Calculate total pages
      const countSnapshot = await getCountFromServer(q)
      const totalItems = countSnapshot.data().count
      setTotalItems(totalItems)
      setTotalPages(Math.ceil(totalItems / ITEMS_PER_PAGE))

      // Add ordering
      q = query(q, orderBy('created_at', 'desc'))

      // If not first page, use startAfter with the correct page reference
      if (currentPage > 1) {
        const prevPageRef = pageRefs[currentPage - 1]
        if (prevPageRef) {
          q = query(q, startAfter(prevPageRef))
        }
      }

      // Add limit
      q = query(q, limit(ITEMS_PER_PAGE))

      const snapshot = await getDocs(q)
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      setItems(items)

      // Store the last document reference for the current page
      if (snapshot.docs.length > 0) {
        const lastDoc = snapshot.docs[snapshot.docs.length - 1]
        setPageRefs(prev => ({ ...prev, [currentPage]: lastDoc }))
      }
    } catch (error) {
      console.error('Error fetching items:', error)
    } finally {
      setLoading(false)
    }
  }

  function clearFilter () {
    setFilters(initialFilterValues)
  }

  function handleFilterChange (name, value) {
    const newFilters = {
      ...filters,
      [name]: value
    }

    if (name === 'state') {
      newFilters.city = ''
    }

    setFilters(newFilters)
  }

  function updateUrlWithFilter (filterValues) {
    const params = new URLSearchParams()
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value) params.append(key, value)
    })
    if (currentPage > 1) {
      params.append('page', currentPage)
    }

    const paramsString = params.toString()

    router.push(`/products${paramsString.length > 0 ? '?' + paramsString : ''}`, undefined, { shallow: true })
  }

  return (<>
    <Seo seo={seo}/>

    <div className="container py-4">
      <h1 className={'m-0 fs-5 mb-4'}>
        Products
      </h1>

      <Row className={'position-relative g-5 g-md-4'}>
        {/* Filters Column */}
        <Col md={3}>
          <div className="d-flex flex-column gap-3 sticky-top pt-2">
            <Form className="d-flex flex-column gap-2">
              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>Price Range</Form.Label>
                <Row>
                  <Col>
                    <Form.Control
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    />
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    />
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>Sofa Type</Form.Label>
                <Form.Select
                  value={filters.sofaType}
                  onChange={(e) => handleFilterChange('sofaType', e.target.value)}
                >
                  <option value="">All Sofa Types</option>
                  {sofaTypes.map(sofaType => (
                    <option
                      key={sofaType.text}
                      value={sofaType.text}
                    >
                      {sofaType.text}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>Color</Form.Label>
                <Form.Select
                  value={filters.color}
                  onChange={(e) => handleFilterChange('color', e.target.value)}
                >
                  <option value="">All Colors</option>
                  {colors.map(color => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>Style</Form.Label>
                <Form.Select
                  value={filters.decorStyle}
                  onChange={(e) => handleFilterChange('decorStyle', e.target.value)}
                >
                  <option value="">All Styles</option>
                  {decorStyles.map(decorStyle => (
                    <option
                      key={decorStyle}
                      value={decorStyle}
                    >
                      {decorStyle}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/*<Form.Group>*/}
              {/*  <Form.Label className={'mb-1 text-textMuted'}>Brand</Form.Label>*/}
              {/*  <Form.Select*/}
              {/*    value={filters.brand}*/}
              {/*    onChange={(e) => handleFilterChange('brand', e.target.value)}*/}
              {/*  >*/}
              {/*    <option value="">All Brands</option>*/}
              {/*    {brands.map(brand => (*/}
              {/*      <option key={brand} value={brand}>{brand}</option>*/}
              {/*    ))}*/}
              {/*  </Form.Select>*/}
              {/*</Form.Group>*/}

              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>State</Form.Label>
                <Form.Select
                  value={filters.state}
                  onChange={(e) => {
                    handleFilterChange('state', e.target.value)
                  }}
                >
                  <option value="">All States</option>
                  {stateOptions.map(state => (
                    <option
                      key={state.slug}
                      value={state.slug}
                    >
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'mb-1 text-muted'}>
                  City
                </Form.Label>
                <Form.Select
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  disabled={!filters.state}
                >
                  <option value="">All Cities</option>
                  {filters.state && (stateOptions.find(state => state.slug === filters.state)?.cities ?? []).map(city => (
                    <option
                      key={city.slug}
                      value={city.slug}
                    >
                      {city.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>

            <Button
              variant={'light'}
              onClick={clearFilter}
            >
              Reset Filters
            </Button>
          </div>
        </Col>

        {/* Products Grid */}
        <Col md={9}>
          {loading ? (
            <>
              <div className={'text-muted'}>
                Loading...
              </div>
            </>
          ) : items.length === 0 ? (
            <div className={' h-100 d-flex align-items-center justify-content-center'}>
              <div
                className="d-flex flex-column gap-3 br-2 p-4 align-items-center justify-content-center"
                style={{ maxWidth: 300 }}
              >
                <div className="fs-6">
                  No Items found
                </div>
                <Button
                  variant={'success'}
                  onClick={clearFilter}
                >
                  Reset filters
                </Button>
              </div>
            </div>
          ) : (
            <div className={'d-flex flex-column gap-2'}>
              {totalItems && (
                <div className={'text-muted mb-1'}>
                  Found {totalItems} item{totalItems > 1 ? 's' : ''}
                </div>
              )}

              <Row>
                {items.map(item => (
                  <Col key={item.id} xs={12} sm={6} lg={4} className="mb-4">
                  <ProductCard
                      item={item}
                    />
                  </Col>
                ))}
              </Row>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <div>
                    page {currentPage} of {totalPages}
                  </div>

                  <Button
                    variant={'light'}
                    disabled={currentPage === 1}
                    onClick={() => {
                      setCurrentPage(prev => Math.max(1, prev - 1))
                      window.scrollTo(0,0)
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faChevronLeft}
                      width={12}
                      height={12}
                    />
                  </Button>

                  <Button
                    variant={'light'}
                    disabled={currentPage === totalPages}
                    onClick={() => {
                      setCurrentPage(prev => Math.min(totalPages, prev + 1))
                      window.scrollTo(0,0)
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      width={12}
                      height={12}
                    />
                  </Button>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </div>
  </>)
}